<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { type ListaItens, type SeletorItem } from '@Lar21/imaginii-web-components/vue'
import { encontrarOpcaoDoCatalogo, preservarEstadoSemSelecaoVazia, prepararEstadoSomenteLeitura } from '@next/compartilhados/catalogos/apresentacao/estado-seletor-catalogo'
import {
  CHAVE_I18N_CATALOGO_PERFIL_DE_PERMISSAO,
  criarEstadoSeletorCatalogoPerfilDePermissao,
  criarPropriedadesPontotelSeletorPerfilDePermissao,
  extrairValorDoSeletorCatalogoPerfilDePermissao,
  filtrarOpcoesPerfilDePermissao,
} from './adaptador-catalogo-perfil-de-permissao'
import { usarCatalogoPerfilDePermissao } from './usar-catalogo-perfil-de-permissao'
import type { FiltrosCatalogoPerfilDePermissao, OpcaoPerfilDePermissao, SelecaoCatalogoPerfilDePermissao } from '../catalogo-perfil-de-permissao.tipos'
type Props = Readonly<{
  modelValue: SelecaoCatalogoPerfilDePermissao
  selecaoMultipla?: boolean
  filtros?: FiltrosCatalogoPerfilDePermissao
  opcoesAdicionais?: readonly OpcaoPerfilDePermissao[]
  predicado?: (opcao: OpcaoPerfilDePermissao) => boolean
  desabilitado?: boolean
  somenteLeitura?: boolean
  permiteSelecionarTodos?: boolean
  permiteLimpar?: boolean
  idsSelecaoAdicional?: readonly string[]
  habilitarDuploClique?: boolean
  label?: string
  placeholder?: string
  mensagemErro?: string | null
}>
const props = withDefaults(defineProps<Props>(), {
  selecaoMultipla: false, desabilitado: false, somenteLeitura: false,
  permiteSelecionarTodos: true, permiteLimpar: true,
  idsSelecaoAdicional: () => [],
  habilitarDuploClique: false, mensagemErro: null,
})
const emit = defineEmits<{
  'update:modelValue': [valor: SelecaoCatalogoPerfilDePermissao]
  'filtrando-itens': [filtro: string]
  'duplo-clique': [opcao: OpcaoPerfilDePermissao]
  'update:selecionouTodos': [selecionouTodos: boolean]
}>()
const { t } = useI18n()
const catalogo = usarCatalogoPerfilDePermissao(computed(() => !props.somenteLeitura))
const opcoes = computed(() => {
  const idsDoCatalogo = new Set(catalogo.itens.value.map(({ id }) => id))
  const opcoesDisponiveis = [
    ...(props.opcoesAdicionais ?? []).filter(({ id }) => !idsDoCatalogo.has(id)),
    ...catalogo.itens.value,
  ]
  const opcoesFiltradas = filtrarOpcoesPerfilDePermissao(opcoesDisponiveis, props.filtros, props.predicado)
  const idsFiltrados = new Set(opcoesFiltradas.map(({ id }) => id))
  const idsSelecaoAdicional = new Set(props.idsSelecaoAdicional)
  const opcoesAdicionais = opcoesDisponiveis.filter(({ id }) => (
    idsSelecaoAdicional.has(id) && !idsFiltrados.has(id)
  ))

  return [...opcoesFiltradas, ...opcoesAdicionais]
})
const selecao = computed<readonly OpcaoPerfilDePermissao[]>(() => {
  const selecaoAtual = props.modelValue === null
    ? []
    : (Array.isArray(props.modelValue)
        ? props.modelValue as readonly OpcaoPerfilDePermissao[]
        : [props.modelValue as OpcaoPerfilDePermissao])
  const idsSelecionados = new Set(selecaoAtual.map(({ id }) => id))
  const idsSelecaoAdicional = new Set(props.idsSelecaoAdicional)
  const selecaoAdicional = opcoes.value.filter(({ id }) => (
    idsSelecaoAdicional.has(id) && !idsSelecionados.has(id)
  ))

  return [...selecaoAtual, ...selecaoAdicional]
})
const estado = computed(() => prepararEstadoSomenteLeitura(
  criarEstadoSeletorCatalogoPerfilDePermissao(
    opcoes.value, selecao.value, props.selecaoMultipla,
  ),
  props.somenteLeitura,
  props.modelValue,
))
const erro = computed(() => catalogo.erro.value
  ? t(`${CHAVE_I18N_CATALOGO_PERFIL_DE_PERMISSAO}.erroBusca`)
  : props.mensagemErro)
const propriedades = computed(() => criarPropriedadesPontotelSeletorPerfilDePermissao({
  traduzir: t,
  ...(props.label !== undefined ? { label: props.label } : {}),
  ...(props.placeholder !== undefined ? { placeholder: props.placeholder } : {}),
  erro: erro.value,
}))
const alterarSelecao = (novoEstado: ListaItens): void => { emit(
  'update:modelValue',
  extrairValorDoSeletorCatalogoPerfilDePermissao(
    preservarEstadoSemSelecaoVazia(novoEstado, estado.value, props.permiteLimpar),
    opcoes.value,
    props.selecaoMultipla,
  ),
); }
const tratarDuploClique = (item: SeletorItem): void => {
  const opcao = encontrarOpcaoDoCatalogo(opcoes.value, item)
  if (opcao) emit('duplo-clique', opcao)
}
</script>
<template>
  <PontotelSeletor
    v-bind="propriedades"
    :model-value="estado"
    :carregar-itens="null"
    :selecao-multipla="selecaoMultipla"
    :desabilitado="desabilitado"
    :modo-exibicao="somenteLeitura"
    :permite-selecionar-todos="permiteSelecionarTodos"
    :habilitar-duplo-clique="habilitarDuploClique"
    :recarregar-todos="!somenteLeitura"
    :recarregando-todos="catalogo.atualizando.value"
    @update:model-value="alterarSelecao"
    @filtrando-itens="emit('filtrando-itens', $event)"
    @duplo-clique="tratarDuploClique"
    @update:selecionou-todos="emit('update:selecionouTodos', $event)"
    @recarregar-todos="catalogo.atualizar"
  />
</template>
